import type { PositionSample } from '../sensors/position';

/**
 * ルートを外れたときに再探索してよいかの判断（描画・通信に依存しない）。
 * 経路探索 API の回数には上限がある（ORS の無料枠は 1日2,000回）ので、無駄な再探索をしない。
 *   - 止まっているとき・GPS の精度が悪いときはしない（止まっていても位置が揺れて「外れた」になる）
 *   - 続けて再探索するときは間隔を延ばす（10秒 → 30秒 → 60秒 → 120秒）。ルートに 60秒乗り続けたら戻す
 *   - 上限に達したら、しばらく（既定 1時間）しない
 */
export type RerouteDecision = { ok: true } | { ok: false; reason: 'stopped' | 'inaccurate' | 'waiting' | 'blocked' };

const INTERVALS = [10_000, 30_000, 60_000, 120_000];
/** これより遅ければ止まっているとみなす [m/s]（約7km/h） */
const MIN_SPEED = 2;
/** これより精度が悪ければ位置を信用しない [m] */
const MAX_ACCURACY = 50;
/** ルートにこれだけ乗り続けたら、間隔を最初に戻す [ms] */
const RESET_AFTER = 60_000;

export class ReroutePolicy {
  private count = 0;
  private lastAt = -Infinity;
  private blockedUntil = 0;
  private onRouteSince: number | null = null;

  decide(sample: PositionSample, now = Date.now()): RerouteDecision {
    if (now < this.blockedUntil) return { ok: false, reason: 'blocked' };
    if ((sample.speed ?? 0) < MIN_SPEED) return { ok: false, reason: 'stopped' };
    if (sample.accuracy > MAX_ACCURACY) return { ok: false, reason: 'inaccurate' };
    // n 回目と n+1 回目の間を INTERVALS[n-1] 空ける（1回目と2回目の間が 10秒、2回目と3回目の間が 30秒…）
    const wait = this.count === 0 ? 0 : INTERVALS[Math.min(this.count - 1, INTERVALS.length - 1)];
    if (now - this.lastAt < wait) return { ok: false, reason: 'waiting' };
    return { ok: true };
  }

  /** 再探索したときに呼ぶ */
  rerouted(now = Date.now()): void {
    this.count++;
    this.lastAt = now;
    this.onRouteSince = null;
  }

  /** 位置の更新ごとに、ルートに乗っているかを知らせる */
  observe(onRoute: boolean, now = Date.now()): void {
    if (!onRoute) {
      this.onRouteSince = null;
      return;
    }
    this.onRouteSince ??= now;
    if (now - this.onRouteSince >= RESET_AFTER) this.count = 0;
  }

  /** 経路探索の上限に達したとき。ms のあいだ再探索しない */
  block(ms = 60 * 60_000, now = Date.now()): void {
    this.blockedUntil = now + ms;
  }

  get blocked(): boolean {
    return Date.now() < this.blockedUntil;
  }

  /** 新しい目的地で案内を始めたとき */
  reset(): void {
    this.count = 0;
    this.lastAt = -Infinity;
    this.onRouteSince = null;
  }
}
